import {Trans} from '@lingui/macro'
import {Navigate, Outlet} from 'react-router'
import styled from 'styled-components'

import {Button, LoadingPage, tokens} from '@pleo-io/telescope'
import {Wand} from '@pleo-io/telescope-icons'

import {useCompanyFeatureEntitlement} from '@product-web/entitlement/use-feature-entitlement'
import {useFlags} from '@product-web/flags'

import {EmptyStatesBudgets} from '~/feature-adoption/empty-states/empty-states-budgets'
import {useReviewManagementMigrationFlag} from '~/feature-review/review-feature-flags/review-feature-flags'
import {Drawer} from '~/generic-ui/drawer'
import {Page} from '~/generic-ui/page'

import {TagBudgetDetailsDrawer} from './tag-budget-details/tag-budget-details-drawer'

import {bff} from '../bff'
import {useActiveBudgetDrawerState} from '../lib/use-active-budget-details'
import {useTagBudgetTracking} from '../lib/use-tag-budget-tracking'

export function BudgetsAuthAndRoutes() {
    const tracking = useTagBudgetTracking()
    const {tagBudgets: hasTagBudgetsFlag} = useFlags()
    const {isEnabled: isReviewPermissionsFlagEnabled, isLoading: isLoadingFlag} =
        useReviewManagementMigrationFlag('REVIEWER_INFO')

    const {data: hasAccessToBudgets, isLoading} = bff.tagBudgetsAuth.getHasAccessToBudgets.useQuery(
        {isFlagEnabled: isReviewPermissionsFlagEnabled},
        {
            enabled: !isLoadingFlag,
        },
    )

    const {isEntitled: hasBudgetsEntitlements, isLoading: isLoadingEntitlements} =
        useCompanyFeatureEntitlement('BUDGETS')

    const {activeBudgetId, isDrawerOpen, openDrawer, closeDrawer} = useActiveBudgetDrawerState()

    // Show loading indicator until we have fetched permissions
    const isLoadingData = isLoading || isLoadingEntitlements || hasTagBudgetsFlag === undefined
    if (isLoadingData) {
        return <LoadingPage />
    }

    const isMissingAccess = Boolean(hasAccessToBudgets) === false
    if (isMissingAccess) {
        return <Navigate to="/" />
    }

    const renderViewDemoButton = () => (
        <Button
            variant="secondary"
            IconRight={Wand}
            onClick={() => {
                tracking.demoBudgetActioned({
                    action: 'clicked',
                    source: 'view_demo_budget_button',
                })
                openDrawer('demo')
            }}
        >
            <Trans>View demo budget</Trans>
        </Button>
    )

    const shouldShowPlanUpgrade = !hasTagBudgetsFlag && !hasBudgetsEntitlements
    if (shouldShowPlanUpgrade) {
        return (
            <>
                <Page.Header>
                    <Page.Title>
                        <Trans>Budgets</Trans>
                    </Page.Title>
                </Page.Header>

                <EmptyStatesBudgetsWrapper>
                    <EmptyStatesBudgets secondaryAction={renderViewDemoButton} />
                </EmptyStatesBudgetsWrapper>

                <Drawer isOpen={isDrawerOpen} onDismiss={closeDrawer} size="auto">
                    <TagBudgetDetailsDrawer
                        activeBudgetId={activeBudgetId}
                        onClose={closeDrawer}
                        shouldShowPlanUpgrade={shouldShowPlanUpgrade}
                    />
                </Drawer>
            </>
        )
    }

    return <Outlet />
}

export const EmptyStatesBudgetsWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-top: ${tokens.spacing48};
    padding-top: ${tokens.spacing48};
    padding-bottom: ${tokens.spacing56};
`

export default BudgetsAuthAndRoutes
